import React, { useState, useRef, useEffect, useCallback } from 'react';
import AdminTables from './adminTables';
import createAxiosInstance from '../../utils/axiosConfig';
import { delete_file, ingested_files } from '../../utils/endpoints';
import Alert from './alerts/alert';
import '../../assets/scss/sidebar.css';
import '../../assets/scss/app.css';
import DeleteIcon from '../../assets/svg/deleteIcon';
import AddIcon from '../../assets/svg/addIcon';
import { Link } from 'react-router-dom';
import NextTabIcon from '../../assets/svg/nextTabIcon';

interface UploadFilesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToIngestion: () => void;
}

interface FileData {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  type: string;
}

const UploadFilesSidebar: React.FC<UploadFilesSidebarProps> = ({ isOpen, onClose, onSwitchToIngestion }) => {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [fileList, setFileList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch files list
  const getFilesList = useCallback(async () => {
    try {
      setLoading(true);
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get('/ingest/list_files');
      setFileList(response?.data?.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
      setAlert({
        message: 'Error loading files. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load files when sidebar opens
  useEffect(() => {
    if (isOpen) {
      getFilesList();
    }
  }, [isOpen, getFilesList]);

  // Drag and drop handlers
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      handleFiles(droppedFiles);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const selectedFiles = Array.from(e.target.files);
      handleFiles(selectedFiles);
    }
  };

  const handleFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const validTypes = ['.pdf', '.docx', '.xlsx', '.doc', '.xls'];
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return validTypes.includes(extension);
    });

    if (validFiles.length !== newFiles.length) {
      setAlert({
        message: 'Some files were rejected. Only PDF, DOCX, XLSX, DOC, and XLS files are allowed.',
        type: 'warning',
        show: true
      });
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      
      const axiosInstance = createAxiosInstance({
        'Content-Type': 'multipart/form-data',
      });

      const response = await axiosInstance.post('/upload/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total 
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
        }
      });

      if (response.status === 200) {
        setAlert({
          message: `${file.name} uploaded successfully!`,
          type: 'success',
          show: true
        });
        
        // Remove from upload progress and refresh file list
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
        
        // Remove from files list
        setFiles(prev => prev.filter(f => f.name !== file.name));
        
        // Refresh the file list
        getFilesList();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setAlert({
        message: `Error uploading ${file.name}. Please try again.`,
        type: 'danger',
        show: true
      });
      
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[file.name];
        return newProgress;
      });
    }
  };

  const removeFile = (fileName: string) => {
    setFiles(prev => prev.filter(file => file.name !== fileName));
  };

  const onButtonClick = () => {
    inputRef.current?.click();
  };

  // Filter button click handler
  const handleFilterClick = (filterType: string) => {
    setSelectedFilter(filterType);
  };

  // Search input handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // File table configuration with filtering
  const getFileTableData = () => {
    let filteredFiles = fileList.map((fileName, index) => ({
      id: index,
      name: fileName,
      size: 'N/A', // Size not available from API
      uploadDate: 'N/A', // Date not available from API
      type: fileName.split('.').pop()?.toUpperCase() || 'Unknown'
    }));

    // Apply file type filter
    if (selectedFilter !== 'ALL') {
      filteredFiles = filteredFiles.filter(file => {
        const fileType = file.type.toUpperCase();
        switch (selectedFilter) {
          case 'CSV':
            return fileType === 'CSV';
          case 'XLSX':
            return fileType === 'XLSX' || fileType === 'XLS';
          case 'WORD':
            return fileType === 'DOCX' || fileType === 'DOC';
          case 'PDF':
            return fileType === 'PDF';
          default:
            return true;
        }
      });
    }

    // Apply search filter
    if (searchTerm.trim()) {
      filteredFiles = filteredFiles.filter(file =>
        file.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filteredFiles;
  };

  const handleDownloadFile = async (fileName: string) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`/ingest/download_file/${fileName.split('/')[2]}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setAlert({
        message: `Error downloading ${fileName}. Please try again.`,
        type: 'danger',
        show: true
      });
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName.split('/')[2]}?`)) {
      return;
    }

    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(delete_file, {
        file_name: fileName
      });

      if (response.data?.error) {
        setAlert({
          message: response.data.error,
          type: 'danger',
          show: true
        });
      } else {
        setAlert({
          message: `${fileName.split('/')[2]} deleted successfully!`,
          type: 'success',
          show: true
        });
        getFilesList(); // Refresh the list
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setAlert({
        message: `Error deleting ${fileName.split('/')[2]}. Please try again.`,
        type: 'danger',
        show: true
      });
    }
  };

  const fileColumns = [
    {
      accessorKey: "name",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>File Name</span>,
      width: "70%",
      cell: ({ row }: { row: any }) => (
        <div style={{ 
          maxWidth: '80%', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'wrap',
          fontSize: '14px'
        }} title={row.original.name}>
          {row.original.name.split('/')[2]}
        </div>
      ),
    },
    // {
    //   accessorKey: "uploadDate",
    //   header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Upload Date</span>,
    //   width: "20%",
    // },
    {
      id: "actions",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Actions</span>,
      width: "10%",
      cell: ({ row }: { row: any }) => (
        <div className="d-flex gap-2 align-items-center">
          <button
            // className="btn btn-sm btn-outline-primary"
            style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
			      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}

            onClick={(e) => {
              e.stopPropagation();
              handleDownloadFile(row.original.name);
            }}
            title="Download"
          >
            <i className="bi bi-download"></i>
          </button>
          <button
            // className="btn btn-sm btn-outline-danger"
            style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteFile(row.original.name);
            }}
            title="Delete"
          >
            <DeleteIcon />
          </button>
        </div>
      ),
    },
  ];

  if (!isOpen) return null;

  return (
    <div>
      <div
        className="offcanvas-backdrop fade show"
        onClick={onClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1050, 
          width: '90vw',
          maxWidth: '1400px',
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <div className="d-flex align-items-center">
            <div 
              className="me-4"
              style={{
                cursor: 'pointer',
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#183FCE',
                padding: '10px 15px',
                // backgroundColor: '#f8f9ff',
                borderRadius: '8px',
                border: '0px solid #183FCE'
              }}
            >
              <i className="bi bi-cloud-upload me-2"></i>
              UPLOAD FILES
            </div>
            <NextTabIcon style={{
                marginRight: '20px'
            }} />
            <div 
              className="me-4"
              onClick={onSwitchToIngestion}
              style={{
                cursor: 'pointer',
                fontSize: '1.25rem',
                fontWeight: 'normal',
                color: '#6c757d',
                padding: '10px 15px',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.color = '#183FCE';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6c757d';
              }}
            >
              <i className="bi bi-robot me-2"></i>
              DATA INGESTION
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        
        <div className="offcanvas-body p-0">
          <div className="row g-0 ">
            {/* Left Side - Drag and Drop Upload */}
            <div className="col-md-5">
                <br />
              <div className="p-4 min-vh-85 d-flex flex-column">
                {/* <h6 className="mb-3" style={{ fontWeight: 'bold', color: '#183FCE' }}>
                  Upload New Files
                </h6> */}
                
                {/* Drag and Drop Area */}
                <div
                  className={`upload-drop-zone flex-grow-1 ${
                    dragActive ? 'drag-active' : ''
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={onButtonClick}
                >
                  <AddIcon />
                  {/* <h5 className="mt-3 mb-2">Drag and Drop Files Here</h5> */}
                  <p className=" mb-3 mt-3">Drag and Drop or Brows to <Link to="#" onClick={onButtonClick} style={{ color: '#183FCE' }}>Upload a New File</Link></p>
                  {/* <button
                    className="btn btn-primary"
                    onClick={onButtonClick}
                  >
                    Browse Files
                  </button> */}
                  <input
                    ref={inputRef}
                    type="file"
                    multiple
                    onChange={handleChange}
                    accept=".pdf,.docx,.xlsx,.doc,.xls"
                    style={{ display: 'none' }}
                  />
                  <p className="mt-3 small bg-white px-2 py-1 rounded-3">
                    Allowed formats: <b>PDF, EXCEL, WORD, CSV</b>
                  </p>
                </div>

                {/* Upload Progress */}
                {(files.length > 0 || Object.keys(uploadProgress).length > 0) && (
                  <div className="mt-3">
                    <h6 style={{ fontWeight: 'bold', color: '#183FCE' }}>Uploading Files</h6>
                    {files.map((file, index) => (
                      <div key={index} className="upload-progress-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small text-truncate" style={{ maxWidth: '200px' }}>
                            {file.name}
                          </span>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeFile(file.name)}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </div>
                        {uploadProgress[file.name] !== undefined && (
                          <div className="upload-progress-bar mt-1">
                            <div
                              className="upload-progress-fill"
                              style={{ width: `${uploadProgress[file.name]}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - File List */}
            <div className="col-md-7">
              <div className="p-4 h-100 overflow-auto">
                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                  <h6 className="mb-0" style={{ fontWeight: 'bold', color: '#183FCE' }}>
                    Uploaded Files
                  </h6>
                  {/* <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={getFilesList}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise"></i> Refresh
                  </button> */}
                </div>

                {/* Pending Files Section */}
                {files.length > 0 && (
                  <div className="mb-4">
                    <div className="d-flex flex-column flex-wrap gap-3">
                      {files.map((file, index) => (
                        <div 
                          key={index} 
                          className="border rounded-3 p-3 bg-white shadow-sm"
                          style={{ 
                            minWidth: '320px', 
                            maxWidth: '100%',
                            position: 'relative'
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center flex-grow-1 me-3">
                              <div 
                                className="d-flex align-items-center justify-content-center me-3"
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px'
                                }}
                              >
                                <i className="bi bi-file-earmark-text" style={{ color: '#183FCE', fontSize: '20px' }}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div 
                                  className="fw-medium text-truncate" 
                                  style={{ maxWidth: '250px', fontSize: '14px' }}
                                  title={file.name}
                                >
                                  {file.name}
                                </div>
                                <div className="text-muted small">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </div>
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <DeleteIcon onClick={() => removeFile(file.name)} />
                            </div>

                            
                          </div>
                          {/* Progress bar */}
                          {uploadProgress[file.name] !== undefined && (
                            <div className="mt-2">
                              <div className="progress" style={{ height: '4px' }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{ 
                                    width: `${uploadProgress[file.name]}%`,
                                    backgroundColor: '#183FCE'
                                  }}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                        </div>
                      ))}
                    </div>
                    <div className='d-flex justify-content-end mt-3'>
                        <button
                            className="btn btn-sm"
                            style={{
                                backgroundColor: '#183FCE',
                                color: 'white',
                                border: 'none',
                                fontSize: '12px',
                                padding: '6px 16px',
                                borderRadius: '6px'
                            }}
                            onClick={() => uploadFile(files[0])}
                            disabled={uploadProgress[files[0].name] !== undefined}
                            >
                            {uploadProgress[files[0].name] !== undefined ? (
                                <>
                                <i className="bi bi-hourglass-split me-1"></i>
                                {uploadProgress[files[0].name]}%
                                </>
                            ) : (
                                <>
                                <i className="bi bi-upload me-1"></i>
                                Upload
                                </>
                            )}
                        </button>
                    </div>
                  </div>
                )}

                <div className='d-flex justify-content-between align-items-center mb-3 mt-3'>
                                 <div className="position-relative" style={{ minWidth: '300px' }}>
                         <input
                             type="text"
                             className="form-control bg-white"
                             placeholder="Search here"
                             value={searchTerm}
                             onChange={handleSearchChange}
                             style={{
                                 borderRadius: '20px',
                                 paddingLeft: '20px',
                                 fontSize: '14px'
                             }}
                         />
                         <i 
                             className="bi bi-search position-absolute"
                             style={{
                                 right: '12px',
                                 top: '50%',
                                 transform: 'translateY(-50%)',
                                 color: '#26397E',
                                 stroke: '#26397E',
                                 fontWeight: 'bolder',
                             }}
                         ></i>
                     </div>
                                         <div className="btn-group" role="group" aria-label="File type filter">
                         {['ALL', 'CSV', 'XLSX', 'WORD', 'PDF'].map((filter) => (
                           <button
                             key={filter}
                             type="button"
                             className={`btn ${selectedFilter === filter ? 'btn-primary' : 'btn-outline-secondary'}`}
                             onClick={() => handleFilterClick(filter)}
                             style={{
                               backgroundColor: selectedFilter === filter ? '#183FCE' : '#f8f9fa',
                               color: selectedFilter === filter ? 'white' : 'black',
                               borderColor: '#dee2e6',
                               fontSize: '14px',
                               fontWeight: '500',
                               transition: 'all 0.2s ease',
                               minWidth: '60px'
                             }}
                             onMouseEnter={(e) => {
                               if (selectedFilter !== filter) {
                                 e.currentTarget.style.backgroundColor = '#e9ecef';
                               }
                             }}
                             onMouseLeave={(e) => {
                               if (selectedFilter !== filter) {
                                 e.currentTarget.style.backgroundColor = '#f8f9fa';
                               }
                             }}
                           >
                             {filter === 'ALL' ? 'All' : filter}
                           </button>
                         ))}
                     </div>
                    
                </div>
                
                <div className="upload-files-table" style={{ height: 'calc(96vh - 200px)', width: '100%', overflow: 'none' }}>
                  <AdminTables
                    data={getFileTableData()}
                    columns={fileColumns}
                    rowSelectionEnabled={false}
                    onRowSelect={() => {}}
                    showSearch={false}
                    isLoading={loading}
                    containerStyle={{
                      width: "100%",
                      overflowX: "auto",
                      height: "96%"
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default UploadFilesSidebar; 